// Simple translation system and hooks.

import type { Locale } from 'date-fns/locale';
import { de, enUS, fr, frCA } from 'date-fns/locale';
import type { StyleProp, TextStyle } from 'react-native';
import { Text } from 'react-native';
// Strings are looked up from translation files and formatted with parameters when needed.
import { allTranslations } from 'translations';
import { useDatastore, useModuleGlobal } from 'util/datastore';
import { moduleGlobalLanguage } from 'util/language';
import type { FormatParams, TLabel } from 'util/stdtypes';
import { emptyR, labelToKey, t, undefFP } from 'util/stdtypes';
import { formatString, getIsLocalhost } from 'util/util';
export const languageEnglish = 'english';
export const languageGerman = 'german';
export const languageFrenchCanadian = 'french_canadian';
export const languageFrenchBelgian = 'french_belgian';
export const languageFrench = 'french';

export function translateLabel({ label, language, formatParams = emptyR }) {
    let extra = {};
    if (formatParams?.singular && formatParams?.plural) {
        if (formatParams.count == 1) {
            extra = {
                noun: translateLabel({ label: formatParams.singular, language }),
                isAre: translateLabel({ label: t('is'), language }),
            };
        } else {
            extra = {
                noun: translateLabel({ label: formatParams.plural, language }),
                isAre: translateLabel({ label: t('are'), language }),
            };
        }
    }

    const effectiveLanguage = language ?? languageEnglish;
    const translations = allTranslations[effectiveLanguage];
    if (!translations) {
        console.error('No translations for ' + effectiveLanguage);
    }

    let translatedText = translations ? translations[label] : label;

    if (label.startsWith('!')) {
        translatedText = label.slice(1);
    }
    if (!translatedText && label.trim()) {
        console.error('No translation for ' + label + ' in ' + effectiveLanguage);
    }
    if (formatParams) {
        translatedText = formatString(translatedText || label, { ...formatParams, ...extra });
    }

    return translatedText || label;
}

export function useLanguage() {
    const datastore = useDatastore();
    return datastore.getLanguage();
}

export function useTranslation(label, formatParams = undefFP) {
    const language = useLanguage();
    if (label == null) return null;

    if (getIsLocalhost() || process.env.NODE_ENV === 'test') {
        const germanTranslation = translateLabel({ label, language: 'german', formatParams });
        const frenchCanadianTranslation = translateLabel({ label, language: 'french_canadian', formatParams });
        const frenchBelgianTranslation = translateLabel({ label, language: 'french_belgian', formatParams });

        // See psi-product/offline/translation/check-translations.js for more info
        if (process.env.NODE_ENV === 'test' && process.env.TRANSLATION_LOG_PATH) {
            /* global global */
            const logEntry = {
                label,
                germanTranslation,
                frenchCanadianTranslation,
                frenchBelgianTranslation,
                testContext: global.testContext || {},
            };
            const fs = require('fs');
            fs.appendFileSync(process.env.TRANSLATION_LOG_PATH, JSON.stringify(logEntry) + '\n', 'utf-8');
        }
    }

    return translateLabel({ label, language, formatParams });
}

export type EllipsizeMode = 'head' | 'middle' | 'tail' | 'clip' | undefined;

export type TranslatableTextProps = {
    numberOfLines?: number;
    ellipsizeMode?: EllipsizeMode;
    text?: string;
    label?: TLabel;
    formatParams?: FormatParams;
    style?: StyleProp<TextStyle>;
    textRef?: React.RefObject<any>;
} & { [key: string]: any };

export function TranslatableText({
    numberOfLines,
    ellipsizeMode,
    text,
    label,
    formatParams,
    style,
    textRef,
    ...props
}: TranslatableTextProps) {
    try {
        const translatedLabel = useTranslation(label, formatParams);
        return (
            <Text ellipsizeMode={ellipsizeMode} numberOfLines={numberOfLines} ref={textRef} style={style} {...props}>
                {translatedLabel || label || text}
            </Text>
        );
    } catch (e) {
        console.error('Error translating ' + label, e);
        return (
            <Text ellipsizeMode={ellipsizeMode} numberOfLines={numberOfLines} ref={textRef} style={style} {...props}>
                {label ? labelToKey(label) : text}
            </Text>
        );
    }
}

export function useClientLocale() {
    const siloLanguage = useModuleGlobal(moduleGlobalLanguage);
    const language = siloLanguage || 'english';
    return getLocaleFromLanguage(language).locale;
}

/** returns the locale and date-fns locale for the given language */
export function getLocaleFromLanguage(language = 'english'): { locale: string; dateFnsLocale: Locale } {
    switch (language) {
        case 'english':
            return { locale: 'en-US', dateFnsLocale: enUS };
        case 'german':
            return { locale: 'de-DE', dateFnsLocale: de };
        case 'french_canadian':
            return { locale: 'fr-CA', dateFnsLocale: frCA };
        case 'french':
        case 'french_belgian':
            return { locale: 'fr-FR', dateFnsLocale: fr };
        default:
            return { locale: 'en-US', dateFnsLocale: enUS };
    }
}
